import React from 'react';
import {motion} from 'framer-motion';
import AnimatedLink from "../../ui/motion/AnimatedLink";
import Heading from "../../common/header/Heading";
import {getProductAccessDeadline} from "../../../util/ProductAccessUtil";

const glassCard = {
    background: 'linear-gradient(145deg, rgba(42, 51, 66, 0.35) 0%, rgba(26, 26, 46, 0.55) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
};

function CourseAndPlatformOverview() {
    const isPlatformAvailable = () => {
        const now = new Date();
        const deadline = getProductAccessDeadline();
        return now >= deadline;
    };

    const platformAvailable = isPlatformAvailable();

    const containerVariants = {
        hidden: {opacity: 0},
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.3
            }
        }
    };

    const itemVariants = {
        hidden: {opacity: 0, y: 50},
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: [0.25, 0.1, 0.25, 1]
            }
        }
    };

    return (
        <section className="py-5 my-lg-5 text-white" id='offerOverview'>
            <div className="mw-md-7xl mx-auto">
                <Heading title='W jaki sposób możemy Ci pomóc?' badgeText='Produkacja' h2={true}
                         subtitle='Wybierz rozwiązanie idealne dopasowane do Twoich potrzeb.'/>
                <motion.div
                    className="row justify-content-center g-12"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{once: true}}
                >
                    {/* Kurs Section */}
                    <motion.div className="col-lg-6 mb-5 mb-lg-0" variants={itemVariants}>
                        <div className="h-100 rounded-4 overflow-hidden position-relative" style={glassCard}>
                            {/* Green accent */}
                            <div style={{
                                height: 3,
                                background: 'linear-gradient(90deg, #2ad167 0%, rgba(42, 209, 103, 0.3) 100%)',
                            }}/>
                            <div className="p-4 d-flex flex-column h-100">
                                <h2 className="fw-bold mb-4 text-gradient-success-warning" style={{fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)'}}>
                                    Kurs Efektywnego Działania&#x2122;
                                </h2>

                                <div className="mb-4 rounded-3 overflow-hidden" style={{border: '1px solid rgba(255, 255, 255, 0.08)'}}>
                                    <img
                                        data-src="https://produkacja.s3.eu-central-1.amazonaws.com/web/oferta-wspolne/ked-showcase-bigger.png"
                                        alt="Kurs Efektywnego Działania - podgląd"
                                        className="img-fluid lazyload"
                                    />
                                </div>

                                <p style={{color: 'rgba(255, 255, 255, 0.55)', fontSize: '0.9rem', lineHeight: 1.7}} className="mb-4">
                                    Odkryj sprawdzone techniki i strategie, które pozwolą Ci zwiększyć produktywność,
                                    uczyć się efektywniej,
                                    lepiej zarządzać czasem i osiągać cele z mniejszym wysiłkiem. Kurs prowadzony przez
                                    doświadczonego eksperta w dziedzinie rozwoju osobistego.
                                </p>

                                <ul className="list-unstyled mb-4">
                                    {[
                                        '12+ godzin materiałów wideo',
                                        'Praktyczne ćwiczenia i zadania',
                                        'Cotygodniowe spotkania z Twórcą Kursu',
                                        'Dostęp do społeczności nastawionych na rozwój nastolatków',
                                        'Certyfikat ukończenia kursu'
                                    ].map((item, i) => (
                                        <li key={i} className="d-flex align-items-center mb-2" style={{fontSize: '0.9rem'}}>
                                            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" className="me-2 flex-shrink-0">
                                                <path d="M9 12l2 2 4-4" stroke="#2ad167" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <circle cx="12" cy="12" r="9" stroke="#2ad167" strokeWidth="2" fill="none" opacity="0.4"/>
                                            </svg>
                                            <span style={{color: 'rgba(255, 255, 255, 0.7)'}}>{item}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-auto pt-3">
                                    <AnimatedLink
                                        to="/oferta/kurs-efektywnego-dzialania"
                                        className="btn d-inline-flex align-items-center fw-semibold"
                                        style={{
                                            background: 'linear-gradient(135deg, #2ad167 0%, #22b859 100%)',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '12px',
                                            padding: '0.65rem 1.5rem',
                                            fontSize: '0.9rem',
                                        }}
                                    >
                                        Sprawdź szczegóły kursu
                                        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" className="ms-2">
                                            <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </AnimatedLink>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Platforma Section */}
                    <motion.div className="col-lg-6" variants={itemVariants}>
                        <div className="h-100 rounded-4 overflow-hidden position-relative" style={glassCard}>
                            {/* Amber accent */}
                            <div style={{
                                height: 3,
                                background: 'linear-gradient(90deg, #f59e0c 0%, rgba(245, 158, 12, 0.3) 100%)',
                            }}/>
                            {/* Coming Soon Badge */}
                            {!platformAvailable && (
                                <div className="position-absolute" style={{top: 12, right: 16, zIndex: 10}}>
                                    <span
                                        className="badge fw-semibold px-3 py-2"
                                        style={{
                                            background: 'rgba(245, 158, 12, 0.15)',
                                            color: '#f59e0c',
                                            fontSize: '0.75rem',
                                            border: '1px solid rgba(245, 158, 12, 0.25)',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        Już wkrótce!
                                    </span>
                                </div>
                            )}

                            <div className="p-4 d-flex flex-column h-100">
                                <h2 className="fw-bold mb-4 text-gradient-warning-success" style={{fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)'}}>
                                    Produkacja OS
                                </h2>

                                <div className="mb-4 rounded-3 overflow-hidden" style={{border: '1px solid rgba(255, 255, 255, 0.08)'}}>
                                    <img
                                        data-src={platformAvailable
                                            ? "https://produkacja.s3.eu-central-1.amazonaws.com/web/oferta-wspolne/platforma-showcase-bigger.png"
                                            : "https://produkacja.s3.eu-central-1.amazonaws.com/web/oferta-wspolne/platforma-showcase-bigger-soon.webp"
                                        }
                                        alt="Produkacja OS - podgląd"
                                        className="img-fluid lazyload"
                                    />
                                </div>

                                <p style={{color: 'rgba(255, 255, 255, 0.55)', fontSize: '0.9rem', lineHeight: 1.7}} className="mb-4">
                                    {platformAvailable
                                        ? "Produkacja OS łączy sztuczną inteligencję z potwierdzonymi metodami nauki. Zautomatyzowane plany nauki, nowoczesny kalendarz, inteligentne fiszki i wsparcie AI pomogą Ci osiągnąć lepsze wyniki w krótszym czasie."
                                        : "Wkrótce udostępnimy Produkacja OS — środowisko, w którym łączymy sztuczną inteligencję z potwierdzonymi metodami nauki. Zautomatyzowane plany nauki, nowoczesny kalendarz, inteligentne fiszki i wsparcie AI pomogą Ci osiągnąć lepsze wyniki w krótszym czasie."
                                    }
                                </p>

                                <ul className="list-unstyled mb-4">
                                    {[
                                        'Spersonalizowane plany nauki dopasowane do poziomu i stylu ucznia',
                                        'Kalendarz ze wszystkimi sprawdzianami i rozplanowaną nauką',
                                        'Tysiące szkolnych zadań z natychmiastową weryfikacją odpowiedzi',
                                        'Rankingi i poziomy - aby motywacja od nauki była jeszcze większa',
                                        'I wiele, wiele więcej...'
                                    ].map((item, i) => (
                                        <li key={i} className="d-flex align-items-center mb-2" style={{fontSize: '0.9rem'}}>
                                            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" className="me-2 flex-shrink-0">
                                                <path d="M9 12l2 2 4-4" stroke="#f59e0c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <circle cx="12" cy="12" r="9" stroke="#f59e0c" strokeWidth="2" fill="none" opacity="0.4"/>
                                            </svg>
                                            <span style={{color: 'rgba(255, 255, 255, 0.7)'}}>{item}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-auto pt-3">
                                    <AnimatedLink
                                        to="/platforma"
                                        className="btn d-inline-flex align-items-center fw-semibold"
                                        style={{
                                            background: 'linear-gradient(135deg, #f59e0c 0%, #d97706 100%)',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '12px',
                                            padding: '0.65rem 1.5rem',
                                            fontSize: '0.9rem',
                                        }}
                                    >
                                        {platformAvailable ? "Poznaj platformę" : "Zapisz się na listę oczekujących"}
                                        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" className="ms-2">
                                            <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </AnimatedLink>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}

export default CourseAndPlatformOverview;
